import React from 'react'
import { cn } from '@/lib/utils'
import { buildCommunityCustomEmojiPublicUrl, parseCommunityCustomEmojiToken } from '../utils/communityEmojiTokens'

type CommunityEmojiIconProps = {
  emoji: string
  className?: string
  loading?: 'lazy' | 'eager'
  showFallback?: boolean
}

const TWEMOJI_VERSION = '16.0.0'
const TWEMOJI_BASE = `https://cdnjs.cloudflare.com/ajax/libs/twemoji/${TWEMOJI_VERSION}/svg`
const VARIATION_SELECTORS = new Set([0xfe0f, 0xfe0e])

function toCodePoints(emoji: string): number[] {
  try {
    return Array.from(String(emoji || ''))
      .map((ch) => ch.codePointAt(0))
      .filter((cp): cp is number => cp != null)
  } catch {
    return []
  }
}

function toTwemojiUrlFromCodePoints(codePoints: number[]): string | null {
  try {
    const slug = (codePoints || []).map((cp) => cp.toString(16)).join('-')
    if (!slug) return null
    return `${TWEMOJI_BASE}/${slug}.svg`
  } catch {
    return null
  }
}

function toTwemojiUrl(emoji: string, opts?: { stripVariationSelectors?: boolean }): string | null {
  const codePoints = toCodePoints(emoji)
  if (!codePoints.length) return null

  const stripVariationSelectors = opts?.stripVariationSelectors !== false
  const normalized = stripVariationSelectors
    ? codePoints.filter((cp) => !VARIATION_SELECTORS.has(cp))
    : codePoints

  return toTwemojiUrlFromCodePoints(normalized)
}

function EmojiIconWithFallback({
  emoji,
  url,
  fallbackUrl,
  className,
  loading,
  isCustom,
}: {
  emoji: string
  url: string
  fallbackUrl?: string | null
  className?: string
  loading: 'lazy' | 'eager'
  isCustom: boolean
}) {
  const [loaded, setLoaded] = React.useState(false)
  const [failed, setFailed] = React.useState(false)
  const [currentUrl, setCurrentUrl] = React.useState(url)
  const triedFallbackRef = React.useRef(false)

  React.useEffect(() => {
    setLoaded(false)
    setFailed(false)
    setCurrentUrl(url)
    triedFallbackRef.current = false
  }, [emoji, url, fallbackUrl])

  const showFallback = !loaded || failed

  return (
    <span
      className={cn(
        'relative inline-flex h-[1.1em] w-[1.1em] items-center justify-center',
        className,
      )}
    >
      {showFallback &&
        (isCustom ? (
          <span
            className="absolute inset-0 rounded-sm bg-foreground/10"
            aria-hidden="true"
          />
        ) : (
          <span
            className="absolute inset-0 flex items-center justify-center leading-none"
            aria-hidden="true"
          >
            {emoji}
          </span>
        ))}
      <img
        src={currentUrl}
        alt={isCustom ? 'Emoji personalizado' : emoji}
        loading={loading}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (!triedFallbackRef.current && fallbackUrl && fallbackUrl !== currentUrl) {
            triedFallbackRef.current = true
            setLoaded(false)
            setFailed(false)
            setCurrentUrl(fallbackUrl)
            return
          }
          setFailed(true)
        }}
        className={cn(
          'absolute inset-0 h-full w-full object-contain transition-opacity',
          loaded && !failed ? 'opacity-100' : 'opacity-0',
        )}
      />
    </span>
  )
}

export function CommunityEmojiIcon({
  emoji,
  className,
  loading = 'lazy',
  showFallback = false,
}: CommunityEmojiIconProps) {
  const token = parseCommunityCustomEmojiToken(emoji)
  if (token) {
    const url = buildCommunityCustomEmojiPublicUrl(token.id)
    if (url) {
      if (showFallback) {
        return (
          <EmojiIconWithFallback
            emoji={emoji}
            url={url}
            className={className}
            loading={loading}
            isCustom
          />
        )
      }

      return (
        <span className={cn('inline-flex items-center justify-center', className)}>
          <img
            src={url}
            alt={emoji}
            loading={loading}
            decoding="async"
            className="h-[1.1em] w-[1.1em] object-contain"
          />
        </span>
      )
    }
  }

  // Twemoji assets variam na presenca de VS16 (FE0F) no nome do arquivo.
  // Estrategia: tenta URL "normalizada" (strip VS16/FE0E) e, se falhar, tenta a URL bruta (mantendo VS16).
  const url = toTwemojiUrl(emoji, { stripVariationSelectors: true })
  const fallbackUrl = url ? toTwemojiUrl(emoji, { stripVariationSelectors: false }) : null

  if (!url) {
    return (
      <span className={cn('inline-flex items-center justify-center', className)}>
        {emoji}
      </span>
    )
  }

  if (showFallback) {
    return (
      <EmojiIconWithFallback
        emoji={emoji}
        url={url}
        fallbackUrl={fallbackUrl && fallbackUrl !== url ? fallbackUrl : null}
        className={className}
        loading={loading}
        isCustom={false}
      />
    )
  }

  return (
    <span className={cn('inline-flex items-center justify-center', className)}>
      <img
        src={url}
        alt={emoji}
        loading={loading}
        decoding="async"
        onError={(event) => {
          const next = fallbackUrl && fallbackUrl !== url ? fallbackUrl : null
          const img = event.currentTarget
          if (!next) return
          if (img.dataset.twemojiFallbackTried === '1') return
          img.dataset.twemojiFallbackTried = '1'
          img.src = next
        }}
        className="h-[1.1em] w-[1.1em] object-contain"
      />
    </span>
  )
}
